import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const Checkbox = ({ checked }) => (
  <span
    className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${
      checked ? 'bg-[#0b7667] border-[#0b7667]' : 'bg-[#f9fafa] border-[#616a79]'
    }`}
  >
    {checked && <span className="material-icons text-[12px] text-[#f9fafa] leading-none">check</span>}
  </span>
)

/**
 * "Filter" trigger + a dropdown panel matching the Figma "Filter dropdown"
 * component: a vertical tab list (one per filter dimension) on the left and
 * that tab's options on the right. Two option types:
 *  - 'static'     — a plain checkbox list (e.g. Status: Online/Offline)
 *  - 'searchable' — search box + "N item selected"/Clear + checkbox list
 *    (e.g. Organization)
 *
 * Selections are a draft until Apply — closing via the X discards changes,
 * matching the Figma footer's explicit Apply button (unlike ColumnSelector,
 * which applies live and has no Apply step).
 *
 * `tabs`: [{ key, label, type: 'static'|'searchable', options: [{value,label}] }]
 * `value`: { [tabKey]: string[] } — currently applied selections
 * `onApply(next)`: called with the full { [tabKey]: string[] } draft on Apply
 */
const FilterModal = ({ tabs, value, onApply }) => {
  const [open, setOpen] = useState(false)
  const [activeTabKey, setActiveTabKey] = useState(tabs[0]?.key)
  const [draft, setDraft] = useState(value)
  const [search, setSearch] = useState('')
  const triggerRef = useRef(null)
  const panelRef = useRef(null)
  const [panelPos, setPanelPos] = useState(null)

  const openPanel = () => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (rect) setPanelPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
    setDraft(value)
    setSearch('')
    setActiveTabKey(tabs[0]?.key)
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e) => {
      if (triggerRef.current?.contains(e.target)) return
      if (panelRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const activeTab = tabs.find((t) => t.key === activeTabKey) ?? tabs[0]
  const activeSelected = draft[activeTab?.key] ?? []

  const toggleOption = (optionValue) => {
    const current = draft[activeTab.key] ?? []
    const next = current.includes(optionValue) ? current.filter((v) => v !== optionValue) : [...current, optionValue]
    setDraft({ ...draft, [activeTab.key]: next })
  }

  const clearActiveTab = () => setDraft({ ...draft, [activeTab.key]: [] })

  const totalSelected = Object.values(value).reduce((sum, arr) => sum + (arr?.length ?? 0), 0)

  const handleApply = () => {
    onApply(draft)
    setOpen(false)
  }

  const filteredOptions = activeTab
    ? activeTab.options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : []

  return (
    <div ref={triggerRef}>
      <button
        className="flex items-center gap-2.5 h-10 px-4 rounded border border-[#dfe4ed] bg-[#f2f4f7] cursor-pointer text-sm font-medium text-[#272d37]"
        onClick={openPanel}
      >
        <span className="material-icons text-xl">filter_list</span>
        Filter
        {totalSelected > 0 && (
          <span className="bg-[#0b7667] text-[#f9fafa] text-xs font-medium rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
            {totalSelected}
          </span>
        )}
        <span className="material-icons text-xl">expand_more</span>
      </button>

      {open &&
        panelPos &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed bg-[#f9fafa] rounded shadow-[0px_4px_32px_0px_rgba(14,17,24,0.1)] flex flex-col w-[524px] z-[1000]"
            style={{ top: panelPos.top, right: panelPos.right }}
          >
            <div className="flex items-center gap-4 border-b border-[#dfe4ed] pl-4 pr-2.5 py-2 shrink-0">
              <span className="flex-1 text-sm font-semibold text-[#272d37]">Filter</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center p-2 rounded-full border-none bg-transparent cursor-pointer hover:bg-[#f2f4f7]"
              >
                <span className="material-icons text-xl text-[#272d37]">close</span>
              </button>
            </div>

            <div className="flex items-start min-h-[320px]">
              <div className="border-r border-[#e5e9f0] flex flex-col w-[180px] shrink-0">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTabKey(tab.key)}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-left cursor-pointer border-none border-l-[3px] ${
                      tab.key === activeTabKey
                        ? 'bg-[#e5f7f3] border-[#0b7667] text-[#0b7667]'
                        : 'bg-transparent border-transparent text-[#272d37]'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-col w-[344px]">
                {activeTab?.type === 'searchable' && (
                  <div className="flex flex-col gap-1.5 pt-2 px-4 shrink-0">
                    <div className="flex items-center gap-2 bg-[#f9fafa] border border-[#b1bbcb] rounded px-2 py-2.5">
                      <span className="material-icons text-xl text-[#616a79]">search</span>
                      <input
                        type="text"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="flex-1 text-sm text-[#272d37] placeholder:text-[#97a1b4] outline-none border-none bg-transparent min-w-0"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="flex-1 text-xs font-medium text-[#272d37]">{activeSelected.length} item selected</span>
                      <button
                        type="button"
                        onClick={clearActiveTab}
                        className="h-9 px-3 rounded border-none bg-transparent cursor-pointer text-sm font-medium text-[#272d37]"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex flex-col py-2 overflow-y-auto max-h-[280px]">
                  {filteredOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => toggleOption(option.value)}
                      className="flex items-center gap-2 h-9 px-4 bg-[#f9fafa] border-none cursor-pointer text-left shrink-0"
                    >
                      <Checkbox checked={activeSelected.includes(option.value)} />
                      <span className="text-sm text-[#272d37]">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end border-t border-[#dfe4ed] px-4 py-2 shrink-0">
              <button
                type="button"
                onClick={handleApply}
                className="h-10 px-4 rounded bg-[#0b7667] text-sm font-medium text-[#f9fafa] cursor-pointer border-none"
              >
                Apply
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}

export default FilterModal
